<template>
	<div id="admin-content-container" class="p-3 container-fluid">
		<div v-if="isFetching && !isChangingPage && !isSorting && !isSearching" class="d-flex justify-content-center h-100">
  			<div class="spinner-border text-secondary my-auto" role="status">
			  <span class="sr-only">Loading...</span>
			</div>
  	 	</div>
		<div v-else-if="isFetched && !isSearched && manifests.total === 0" class="alert alert-secondary fade show">
			{{ __('translations.message.no_documents_found') }}
		</div>
		<div v-else-if="error" class="alert alert-danger fade show">
			{{ __('translations.message.generic_error') }}
		</div>
		<div v-else class="card" style="overflow-y: auto;">
			<div class="card-body">
				<div v-if="success">
					<div v-if="allDocuments" class="alert alert-success fade show" role="alert">
					  {{ __('translations.message.documents_added_to_workspace') }}
					</div>
					<div v-else class="alert alert-success fade show" role="alert">
					  {{ __('translations.message.documents_removed_from_workspace') }}
					</div>
				</div>
				<div class="d-flex justify-content-left align-items-center mb-3">
		    		<span class="mr-3" v-if="selectedManifestIds.length === 1">{{ selectedManifestIds.length }} {{__('translations.label.single_document_selected') }}</span>
		    		<span class="mr-3" v-if="selectedManifestIds.length > 1">{{ selectedManifestIds.length }} {{__('translations.label.multiple_documents_selected') }}</span>
		    		<button class="btn btn-secondary mr-3" v-if="selectedManifestIds.length > 0" @click="clearSelection">{{__('translations.button.clear_selection') }}</button>
		    		<button v-if="allDocuments" class="btn btn-primary" :disabled="selectedManifestIds.length === 0" @click="addManifestsToWorkspace">{{ __('translations.button.add_to_my_documents') }}</button>
		    		<button v-else class="btn btn-primary" :disabled="selectedManifestIds.length === 0" @click="removeManifestsFromWorkspace">{{ __('translations.button.remove_from_my_documents') }}</button>
		    	</div>
		    	<hr/>
				<div class="d-flex justify-content-between mb-3">
					<div class="d-flex align-items-center w-60">
						<div class="input-group input-group-sm">
						  <div class="input-group-prepend">
						    <label class="input-group-text" for="inputGroupSelect01">{{ __('translations.label.filter_by') }}</label>
						  </div>
						  <select class="custom-select custom-select-sm mr-2" v-model="metadataFilter.label">
						  	<option value="no-filter"></option>
							<option v-for="(metadata, index) in metadatas" :value="metadata.label" :key="'metadata-label-' + index">{{ metadata.label }}</option>
						  </select>
						  <select class="custom-select custom-select-sm" v-model="metadataFilter.value" @change="filterManifests">
							<option v-for="(value, index) in metadataValues" :value="value" :key="'metadata-value-' + index">{{ value }}</option>
						  </select>
						</div>
					</div>
					<div>
						<div class="input-group">
						  <input type="text" class="form-control form-control-sm" v-model="searchQuery" @keyup.enter="searchManifests" />
						  <div class="input-group-append">
						    <span class="input-group-text"><i class="fas fa-search"></i></span>
						  </div>
						</div>
					</div>
				</div>
				<div v-if="isSearching" class="d-flex justify-content-center">
		  			<div class="spinner-border text-secondary" role="status">
					  <span class="sr-only">Loading...</span>
					</div>
		  	 	</div>
		  	 	<div v-else-if="isSearched && manifests.total === 0" class="alert alert-secondary fade show mt-3 mb-0">
		  			{{ __('translations.message.no_documents_found') }}
		  	 	</div>
		  	 	<div v-else>
			      <div class="d-flex justify-content-between mb-3">
		    		<div class="d-flex align-items-center">
		    			<span>{{ manifests.from }} – {{ manifests.to }}{{ __('translations.label.of') }}{{ manifests.total }}</span>
		    		</div>
			    	<div class="d-flex">
						<button :disabled="manifests.current_page <= 1" class="btn mr-2" @click="goToPage(1)"><i class="fas fa-angle-double-left" style="vertical-align: middle;"></i></button>
						<button :disabled="manifests.current_page <= 1" class="btn mr-2" @click="goToPage(parseInt(manifests.current_page) - 1)"><i class="fas fa-angle-left" style="vertical-align: middle;"></i></button>
						<div class="justify-content-right" style="color: #3f5864">
							{{ __('translations.label.page') }}<input type="text" v-model="manifests.current_page" @keyup.enter="goToPage(manifests.current_page)" :disabled="manifests.last_page === 1" class="form-control d-inline-block mx-2" style="width: 50px" />{{ __('translations.label.of') }}{{manifests.last_page}}
						</div>
						<button :disabled="manifests.current_page >= manifests.last_page" class="btn ml-2" @click="goToPage(parseInt(manifests.current_page) + 1)"><i class="fas fa-angle-right" style="vertical-align: middle;"></i></button>
						<button :disabled="manifests.current_page >= manifests.last_page" class="btn ml-2" @click="goToPage(manifests.last_page)"><i class="fas fa-angle-double-right" style="vertical-align: middle;"></i></button>
					</div>
			      </div>
			      <div v-if="isChangingPage || isSorting" class="d-flex justify-content-center">
		  			<div class="spinner-border text-secondary" role="status">
					  <span class="sr-only">Loading...</span>
					</div>
		  	 	  </div>
				  <b-table-simple striped bordered responsive class="table table-sm " style="overflow-x: none;" v-else-if="isFetched && manifests.total > 0">
		  			<b-thead>
		    			<b-tr>
							<b-th scope="col" class="align-middle" width="40px">
								<b-form-checkbox
						          v-model="isSelectedAll"
						          :indeterminate="isIndeterminate"
						          @change="toggleAll"></b-form-checkbox>
							</b-th>
							<b-th scope="col" @click="sortManifests('label')" :aria-sort="ariaSort('label')" class="align-middle" width="25%">{{ __('translations.title.document') }}</b-th>
							<b-th scope="col" @click="sortManifests('canvases_count')" :aria-sort="ariaSort('canvases_count')" class="align-middle text-center" width="80px">{{ __('translations.title.pages') }}</b-th>
							<template v-for="metadata in metadatas">
								<b-th scope="col" @click="sortManifests(metadata.label)" :aria-sort="ariaSort(metadata.label)" class="align-middle">{{ metadata.label }}</b-th>	
							</template>
							<b-th scope="col" class="align-middle text-center" width="110px">{{ __('translations.title.action') }}</b-th>
						</b-tr>
		  			</b-thead>
		  			<tbody>
		  				<tr v-for="manifest in manifests.data">
		  					<td scope="row" class="align-middle" style="padding: 0.3rem!important;">
		  						<b-form-checkbox v-model="manifest.selected" @change="toggleManifest(manifest)"></b-form-checkbox>
	  						</td>
		  					<td style="padding: 0.3rem!important;">
		  						<div class="d-flex flex-row">
		  							<img :src="manifest.thumbnail" :alt="manifest.label" width="100px"/>
		  							<div class="flex-grow ml-2">
		  								{{ manifest.label }}<br/>
		  								<span class="text-muted" style="font-size:14px;">{{ manifest.description }}</span>
		  							</div>
		  						</div>
	  						</td>
		  					<td style="padding: 0.3rem!important;" class="text-center">{{ manifest.canvases_count }}</td>
		  					<template v-for="metadata in metadatas">
		  						<td style="padding: 0.3rem!important;">{{ manifest[metadata.label] }}</td>
		  					</template>
		  					<td style="padding: 0.3rem!important;" class="text-center">
								<router-link :to="{name: 'canvases', params: {cId: manifest.collection.id, mId: manifest.id}}">
									<button class="btn btn-sm btn-primary">{{ __('translations.button.open') }}</button>
								</router-link>
							</td>
		  				</tr>
		  			</tbody>
		  		  </b-table-simple>
		  		  <div class="d-flex justify-content-between mt-3">
		    		<div class="d-flex align-items-center">
		    			<span>{{ manifests.from }} – {{ manifests.to }}{{ __('translations.label.of') }}{{ manifests.total }}</span>
		    		</div>
			    	<div class="d-flex">
				    	<button :disabled="manifests.current_page <= 1" class="btn mr-2" @click="goToPage(1)"><i class="fas fa-angle-double-left" style="vertical-align: middle;"></i></button>
						<button :disabled="manifests.current_page <= 1" class="btn mr-2" @click="goToPage(parseInt(manifests.current_page) - 1)"><i class="fas fa-angle-left" style="vertical-align: middle;"></i></button>
						<div class="justify-content-right" style="color: #3f5864">
							Page <input type="text" v-model="manifests.current_page" @keyup.enter="goToPage(manifests.current_page)" :disabled="manifests.last_page === 1" class="form-control d-inline-block mx-2" style="width: 50px" />{{ __('translations.label.of') }}{{manifests.last_page}}
						</div>
						<button :disabled="manifests.current_page >= manifests.last_page" class="btn ml-2" @click="goToPage(parseInt(manifests.current_page) + 1)"><i class="fas fa-angle-right" style="vertical-align: middle;"></i></button>
						<button :disabled="manifests.current_page >= manifests.last_page" class="btn ml-2" @click="goToPage(manifests.last_page)"><i class="fas fa-angle-double-right" style="vertical-align: middle;"></i></button>
					</div>
			      </div>
			      <hr/>
			      <div class="d-flex justify-content-left align-items-center">
		    		<span class="mr-3" v-if="selectedManifestIds.length === 1">{{ selectedManifestIds.length }} {{__('translations.label.single_document_selected') }}</span>
		    		<span class="mr-3" v-if="selectedManifestIds.length > 1">{{ selectedManifestIds.length }} {{__('translations.label.multiple_documents_selected') }}</span>
					<button class="btn btn-secondary mr-3" v-if="selectedManifestIds.length > 0" @click="clearSelection">{{__('translations.button.clear_selection') }}</button>
		    		<button v-if="allDocuments" class="btn btn-primary" :disabled="selectedManifestIds.length === 0" @click="addManifestsToWorkspace">{{ __('translations.button.add_to_my_documents') }}</button>
		    		<button v-else class="btn btn-primary" :disabled="selectedManifestIds.length === 0" @click="removeManifestsFromWorkspace">{{ __('translations.button.remove_from_my_documents') }}</button>
	    		  </div>
			    </div>
			</div>
		</div>
	</div>
</template>

<script>
import ExplorerMixin from '../../mixins/ExplorerMixin'

export default {
	mixins: [ExplorerMixin],
	props: ['allDocuments'],
	methods: {
		fetchManifests() {
			this.isFetching = true;
			this.isFetched = false;
			this.isSearched = false;
			this.error = false;
			
			var params = this.$route.query;
			params.filterByUser = this.allDocuments ? 0 : 1;
			
    		axios.get('/fetchManifests', {params: params}).then(response => {
    			this.manifests = response.data;
    			this.isFetching = false;
    			this.isFetched = true;
    			
				/* If the manifest ID is found in selectedManifestIds array,
				set the selected state to `true`, otherwise `false`
			    to restore the selected state */
				this.manifests.data.forEach(manifest => {
					Vue.set(manifest, 'selected', !!this.selectedManifestIds.find(manifestId => manifestId === manifest.id));
				})
    			
    			// To display the not found message under the filter and search fields.
    			this.isSearched = this.isSearching;
    			
    			this.resetFlags();
            }).catch(err => {
            	this.isFetching = false;
    			this.isFetched = true;
            	this.error = true;
            })
    	},
		addManifestsToWorkspace() {
			axios.post('/addUserManifests', {manifestIds: this.selectedManifestIds}).then(response => {
				this.selectManifests = [];
				this.success = true;
				this.clearSelection();
				
				// Scrol to the top, so that the displayed message can be seen.
				window.scrollTo(0, 0);
				
				setTimeout(() => {
					this.success = false;
				}, 5000);
			})
		},
		removeManifestsFromWorkspace() {
			axios.post('/removeUserManifests', {manifestIds: this.selectedManifestIds}).then(response => {
				this.selectManifests = [];
				this.success = true;
				this.clearSelection();
				this.fetchManifests();
				
				// Scrol to the top, so that the displayed message can be seen.
				window.scrollTo(0, 0);
				
				setTimeout(() => {
					this.success = false;
				}, 5000);
			})
		},
	}
}
</script>